import { useCallback, useEffect, useMemo, useState } from 'react';
import { jobTrackerApi } from '../services/jobTrackerApi';
import { STAGE_IDS } from '../constants';

/**
 * Owns the board's data: loading, CRUD and filtering.
 *
 * Stage moves apply optimistically so dragging feels instant; if the server
 * rejects the move the previous state is restored and an error surfaces.
 */
export const useJobTracker = () => {
  const [jobs, setJobs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [saving, setSaving]   = useState(false);

  const [search, setSearch]         = useState('');
  const [stageFilter, setStage]     = useState('all');
  const [dateFilter, setDateFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setJobs(await jobTrackerApi.list());
    } catch (e) {
      setError(e.message || 'Could not load your applications.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createJob = useCallback(async (data) => {
    setSaving(true);
    try {
      const created = await jobTrackerApi.create(data);
      setJobs(prev => [created, ...prev]);
      return created;
    } finally { setSaving(false); }
  }, []);

  const updateJob = useCallback(async (id, data) => {
    setSaving(true);
    try {
      const updated = await jobTrackerApi.update(id, data);
      setJobs(prev => prev.map(j => (j._id === id ? updated : j)));
      return updated;
    } finally { setSaving(false); }
  }, []);

  const deleteJob = useCallback(async (id) => {
    const snapshot = jobs;
    setJobs(prev => prev.filter(j => j._id !== id));   // optimistic
    try {
      await jobTrackerApi.remove(id);
    } catch (e) {
      setJobs(snapshot);
      setError(e.message || 'Could not delete that application.');
      throw e;
    }
  }, [jobs]);

  const moveJob = useCallback(async (id, stage) => {
    const current = jobs.find(j => j._id === id);
    if (!current || current.stage === stage) return;

    // Land above everything already in the destination column.
    const topOrder = jobs
      .filter(j => j.stage === stage)
      .reduce((min, j) => Math.min(min, j.order ?? 0), 0);
    const order = topOrder - 1;

    const snapshot = jobs;
    setJobs(prev => prev.map(j => (j._id === id ? { ...j, stage, order } : j)));

    try {
      const updated = await jobTrackerApi.moveStage(id, stage, order);
      setJobs(prev => prev.map(j => (j._id === id ? updated : j)));
      return updated;
    } catch (e) {
      setJobs(snapshot);
      setError(e.message || 'Could not move that application.');
      return null;   // caller must not present the move as having happened
    }
  }, [jobs]);

  /* ── Filtering ─────────────────────────────────────────────────────────── */
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const cutoff = dateFilter === 'all' ? null
      : new Date(Date.now() - Number(dateFilter) * 24 * 60 * 60 * 1000);

    return jobs.filter((job) => {
      if (stageFilter !== 'all' && job.stage !== stageFilter) return false;

      if (cutoff) {
        const stamp = job.appliedDate || job.createdAt;
        if (!stamp || new Date(stamp) < cutoff) return false;
      }

      if (!term) return true;
      return [job.title, job.company, job.location, job.notes, job.contactName]
        .some(field => (field || '').toLowerCase().includes(term));
    });
  }, [jobs, search, stageFilter, dateFilter]);

  /** { applied: [...], screening: [...], ... } in column order. */
  const byStage = useMemo(() => {
    const groups = Object.fromEntries(STAGE_IDS.map(id => [id, []]));
    filtered.forEach((job) => { (groups[job.stage] || groups.applied).push(job); });
    STAGE_IDS.forEach((id) => {
      groups[id].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)
        || new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
    });
    return groups;
  }, [filtered]);

  const hasFilters = search.trim() !== '' || stageFilter !== 'all' || dateFilter !== 'all';
  const clearFilters = useCallback(() => { setSearch(''); setStage('all'); setDateFilter('all'); }, []);

  return {
    jobs, filtered, byStage,
    loading, saving, error, setError,
    reload: load,
    createJob, updateJob, deleteJob, moveJob,
    search, setSearch,
    stageFilter, setStageFilter: setStage,
    dateFilter, setDateFilter,
    hasFilters, clearFilters,
  };
};
